import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import { listReferenceRows, upsertReferenceRow, deleteReferenceRow } from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';
import AdminPagination from './AdminPagination';
import AdminSearchSelect from './AdminSearchSelect';
import { toBool } from './refDataConfigs';
import { useAirlineLogoMapVersion } from '../utils/airlineLogoMap';
import './Admin.css';
import './AdminReferenceData.css';

const DEFAULT_PAGE_SIZE = 50;

/** Composite keys are '|'-joined, matching ReferenceDataService. */
export function rowKey(config, row) {
  return config.keyColumns.map(c => row[c] == null ? '' : String(row[c])).join('|');
}

/** Row (string-valued) → form state (booleans for checkboxes, strings otherwise). */
function rowToForm(config, row) {
  const form = {};
  config.columns.forEach(col => {
    if (col.edit === false) return;
    const v = row[col.key];
    form[col.key] = col.edit === 'checkbox' ? toBool(v) : (v == null ? '' : String(v));
  });
  return form;
}

/**
 * Form state → `values` for upsertReferenceRow. Checkboxes become "1"/"0",
 * everything else is trimmed. `locked` is not a value: it is the top-level
 * `lock` flag, so it is stripped here and sent separately.
 */
export function formToValues(config, form) {
  const values = {};
  config.columns.forEach(col => {
    if (col.edit === false || col.key === 'locked') return;
    const v = form[col.key];
    if (col.edit === 'checkbox') {
      values[col.key] = v ? '1' : '0';
    } else {
      const s = v == null ? '' : String(v).trim();
      values[col.key] = col.upper ? s.toUpperCase() : s;
    }
  });
  return values;
}

function validate(config, values) {
  for (const col of config.columns) {
    if (col.required && col.edit !== false && col.edit !== 'checkbox' && !values[col.key]) {
      return `${col.label} is required`;
    }
  }
  return null;
}

function cellValue(col, row) {
  if (col.render) return col.render(row);
  const v = row[col.key];
  if (col.edit === 'checkbox') {
    return toBool(v)
      ? <span className="admin-status-badge active">Yes</span>
      : <span className="admin-ref-no">—</span>;
  }
  if (v == null || v === '') return <span className="gap">—</span>;
  return String(v);
}

/**
 * Generic searchable / sortable / paged grid with inline add, edit and delete
 * for one reference table. Everything table-specific lives in `config`
 * (see refDataConfigs.js).
 */
function AdminRefTable({ config, debounceMs = 300, pageSize: initialPageSize }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize || config.pageSize || DEFAULT_PAGE_SIZE);
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [activeFilters, setActiveFilters] = useState(() => new Set());
  const [sort, setSort] = useState(config.defaultSort || { by: config.keyColumns[0], dir: 'asc' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({});
  const [extra, setExtra] = useState({});
  const [saving, setSaving] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const requestIdRef = useRef(0);

  // Logo preview cells resolve aliases through the runtime map.
  useAirlineLogoMapVersion();

  // Debounce the search box → q; a new q resets to page 0.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = qInput.trim();
      setQ(prev => (prev === next ? prev : next));
      setPage(0);
    }, debounceMs);
    return () => clearTimeout(t);
  }, [qInput, debounceMs]);

  const filters = useMemo(() => {
    const out = {};
    (config.filters || []).forEach(f => {
      if (activeFilters.has(f.key)) Object.assign(out, f.filter);
    });
    return out;
  }, [config.filters, activeFilters]);

  const skipLoad = config.requireQuery && !q;

  const load = useCallback(async () => {
    if (skipLoad) {
      setRows([]);
      setTotal(0);
      setError(null);
      return;
    }
    const id = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const resp = await listReferenceRows({
        table: config.table,
        q: q || undefined,
        filters: Object.keys(filters).length ? filters : undefined,
        sortBy: sort.by,
        sortDir: sort.dir,
        page,
        pageSize
      }, getAccessToken());
      if (id !== requestIdRef.current) return; // stale response
      if (isApiSuccess(resp)) {
        setRows(resp.rows || []);
        setTotal(typeof resp.totalCount === 'number' ? resp.totalCount : -1);
      } else {
        setError(getFirstApiError(resp) || `Failed to load ${config.title}`);
      }
    } catch (e) {
      if (id === requestIdRef.current) setError(`Failed to load ${config.title}`);
    } finally {
      if (id === requestIdRef.current) setLoading(false);
    }
  }, [config.table, config.title, q, filters, sort, page, pageSize, skipLoad]);

  useEffect(() => { load(); }, [load]);

  const toggleFilter = (key) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setPage(0);
  };

  const toggleSort = (col) => {
    if (col.sortable === false || col.render) return;
    setSort(prev => ({
      by: col.key,
      dir: prev.by === col.key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
    setPage(0);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setAdding(false);
    setForm({});
    setExtra({});
  };

  const startEdit = (row) => {
    setAdding(false);
    setEditingKey(rowKey(config, row));
    setForm(rowToForm(config, row));
    setExtra({});
    setActionMsg(null);
  };

  const startAdd = () => {
    setEditingKey(null);
    setAdding(true);
    setForm({ ...config.emptyForm });
    setExtra({});
    setActionMsg(null);
  };

  const save = async () => {
    const values = formToValues(config, form);
    const problem = validate(config, values);
    if (problem) {
      setActionMsg({ type: 'error', text: problem });
      return;
    }
    setSaving(true);
    try {
      const token = getAccessToken();
      const payload = {
        table: config.table,
        values,
        lock: form.locked !== undefined ? !!form.locked : true
      };
      if (!adding) payload.key = editingKey;
      const resp = await upsertReferenceRow(payload, token);
      if (!isApiSuccess(resp)) {
        setActionMsg({ type: 'error', text: getFirstApiError(resp) || 'Save failed' });
        return;
      }
      if (config.afterSave) {
        // afterSave throws to report a follow-up failure (e.g. logo upload).
        await config.afterSave({ values, key: adding ? null : editingKey, extra, row: resp.row, token });
      }
      const savedKey = resp.key || (adding ? config.keyColumns.map(c => values[c] || '').join('|') : editingKey);
      setActionMsg({ type: 'success', text: `Saved ${savedKey}` });
      cancelEdit();
      load();
    } catch (e) {
      setActionMsg({ type: 'error', text: (e && e.message) || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    const key = rowKey(config, row);
    if (!window.confirm(`Delete ${key} from ${config.title}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const payload = { table: config.table, key };
      if (config.deleteNeedsConfirmFlag) payload.confirm = true;
      const resp = await deleteReferenceRow(payload, getAccessToken());
      if (!isApiSuccess(resp)) {
        setActionMsg({ type: 'error', text: getFirstApiError(resp) || 'Delete failed' });
        return;
      }
      setActionMsg({ type: 'success', text: `Deleted ${key}` });
      if (editingKey === key) cancelEdit();
      load();
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Delete failed' });
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const renderField = (col) => {
    const readOnly = !adding && col.readOnlyOnEdit;
    const id = `ref-${config.key}-${col.key}`;
    const help = col.help ? <span className="admin-ref-help">{col.help}</span> : null;
    if (col.edit === 'checkbox') {
      return (
        <label key={col.key} className="admin-edit-form-checkbox" htmlFor={id} title={col.help || undefined}>
          <input id={id} type="checkbox" aria-label={col.label} checked={!!form[col.key]} onChange={e => setField(col.key, e.target.checked)} />
          {col.label}
        </label>
      );
    }
    if (col.edit === 'typeSelect') {
      return (
        <label key={col.key} className="admin-ref-field">
          {col.label}{col.required ? ' *' : ''}
          <AdminSearchSelect
            value={form[col.key] || ''}
            ariaLabel={col.label}
            disabled={readOnly}
            onSelect={opt => setField(col.key, opt ? opt.value : '')}
          />
          {help}
        </label>
      );
    }
    if (col.edit === 'select') {
      return (
        <label key={col.key} htmlFor={id}>
          {col.label}{col.required ? ' *' : ''}
          <select id={id} aria-label={col.label} value={form[col.key] || ''} disabled={readOnly} onChange={e => setField(col.key, e.target.value)}>
            <option value="">—</option>
            {(col.options || []).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          {help}
        </label>
      );
    }
    return (
      <label key={col.key} htmlFor={id}>
        {col.label}{col.required ? ' *' : ''}
        <input
          id={id}
          aria-label={col.label}
          type={col.edit === 'number' ? 'number' : 'text'}
          value={form[col.key] == null ? '' : form[col.key]}
          maxLength={col.maxLength}
          readOnly={readOnly}
          className={readOnly ? 'admin-ref-readonly' : undefined}
          onChange={e => setField(col.key, col.upper ? e.target.value.toUpperCase() : e.target.value)}
        />
        {help}
      </label>
    );
  };

  const editForm = (
    <div className="admin-edit-form">
      {config.columns.filter(c => c.edit !== false).map(renderField)}
      {config.renderEditExtra && config.renderEditExtra({ form, setForm, extra, setExtra, adding })}
      <div className="admin-edit-form-actions">
        <button type="button" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={cancelEdit} disabled={saving}>Cancel</button>
      </div>
    </div>
  );

  const colCount = config.columns.length + 1;

  return (
    <div className="admin-ref-table">
      <div className="admin-toolbar">
        <input
          type="text"
          className="admin-ref-search"
          placeholder={config.searchHint || 'Search…'}
          aria-label={`Search ${config.title}`}
          value={qInput}
          onChange={e => setQInput(e.target.value)}
        />
        {(config.filters || []).length > 0 && (
          <div className="admin-status-filter" role="group" aria-label="Filters">
            {config.filters.map(f => (
              <button
                key={f.key}
                type="button"
                className={`admin-filter-chip${activeFilters.has(f.key) ? ' active' : ''}`}
                aria-pressed={activeFilters.has(f.key)}
                onClick={() => toggleFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
        {config.canAdd !== false && (
          <button type="button" onClick={startAdd} disabled={adding}>Add</button>
        )}
        <button type="button" onClick={load} disabled={loading || skipLoad}>Refresh</button>
        <span className="admin-toolbar-meta">
          {skipLoad ? config.searchHint : loading ? 'Loading…' : total >= 0 ? `${total.toLocaleString()} rows` : `${rows.length} rows shown`}
        </span>
      </div>

      {actionMsg && (
        <div className={`admin-action-message admin-action-${actionMsg.type}`} role="status">
          {actionMsg.text}
        </div>
      )}

      {error && (
        <div className="admin-error">
          <p>{error}</p>
          <button type="button" onClick={load}>Retry</button>
        </div>
      )}

      {!error && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                {config.columns.map(col => {
                  const sortable = col.sortable !== false && !col.render;
                  const isSorted = sort.by === col.key;
                  return (
                    <th
                      key={col.key}
                      className={sortable ? 'sortable' : undefined}
                      aria-sort={isSorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      onClick={sortable ? () => toggleSort(col) : undefined}
                    >
                      {col.label}{isSorted ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  );
                })}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {adding && (
                <tr className="receivers-row">
                  <td colSpan={colCount}>{editForm}</td>
                </tr>
              )}
              {!skipLoad && !loading && rows.length === 0 && !adding && (
                <tr>
                  <td colSpan={colCount} className="unknown-empty">No rows</td>
                </tr>
              )}
              {rows.map(row => {
                const key = rowKey(config, row);
                const isEditing = editingKey === key;
                return [
                  <tr key={key} className={isEditing ? 'expanded' : undefined}>
                    {config.columns.map(col => (
                      <td key={col.key} data-label={col.label} className={col.mono ? 'admin-ref-mono' : undefined}>
                        {cellValue(col, row)}
                      </td>
                    ))}
                    <td data-label-none>
                      <div className="admin-ref-actions">
                        <button type="button" onClick={() => (isEditing ? cancelEdit() : startEdit(row))}>
                          {isEditing ? 'Cancel' : 'Edit'}
                        </button>
                        {config.canDelete !== false && (
                          <button
                            type="button"
                            className="admin-danger-button"
                            disabled={saving}
                            onClick={() => remove(row)}
                          >
                            {config.deleteLabel || 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>,
                  isEditing ? (
                    <tr key={`${key}-edit`} className="receivers-row">
                      <td colSpan={colCount}>{editForm}</td>
                    </tr>
                  ) : null
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      {!error && !skipLoad && (
        <AdminPagination
          page={page}
          pageSize={pageSize}
          totalCount={total}
          rowCount={rows.length}
          disabled={loading}
          onPageChange={setPage}
          onPageSizeChange={(n) => { setPageSize(n); setPage(0); }}
        />
      )}
    </div>
  );
}

export default AdminRefTable;
